import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import SpiderChartContainer from "../../pages/spiderChart/spiderChartContainer";
import FilledFormPDF from "../pdfFormat/filledFormPdf";
import GcchfHealthReportPdf from "../pdfFormat/GcchfHealthReportPdf";
import PersonalHealthReportPdf from "../pdfFormat/PersonalHealthReportPdf";
import BASE_URL from "../config/appconfig";
import usePageMeta from "../../utils/usePageMeta";

const font = "Gotham, sans-serif";
const SECTION_COLORS = ["#ED1C2E", "#263842", "#2E7D32", "#9C27B0", "#FF9800"];

const getNarrative = (name, score) => {
  if (score >= 4)
    return `Your community shows a vibrant, the Gospel and Church Health Framework depth in ${name}, marked by a clear dependence on Scripture and prayer.`;
  if (score >= 3)
    return `Growth is visible in ${name}; focus now on decentralising ministry and empowering members to lead in their own spheres.`;
  return `Opportunities for growth are visible in ${name}; consider how to better equip members to engage cultural objections with grace.`;
};

const computeSectionAverages = (responses) => {
  const sectionMap = {};
  const sectionOrder = [];

  responses.forEach((r) => {
    const sectionName = r.section?.name;
    if (!sectionName) return;

    const selectedOption = r.question?.options?.find(
      (opt) => opt._id === r.objective?.selected_option_id,
    );
    if (!selectedOption) return;

    if (!sectionMap[sectionName]) {
      sectionMap[sectionName] = { values: [] };
      sectionOrder.push(sectionName);
    }
    sectionMap[sectionName].values.push(selectedOption.option_value);
  });

  return sectionOrder.map((name, idx) => {
    const { values } = sectionMap[name];
    const score = parseFloat(
      (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
    );
    return {
      name,
      score,
      color: SECTION_COLORS[idx] || "#666",
      description: getNarrative(name, score),
    };
  });
};

const CTCAP_EMAIL = "churchhealth@citytocityasiapacific.com";

const Feedback = () => {
  usePageMeta(
    "Feedback",
    "View your Gospel and Church Health Framework results.",
  );
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [reflectionResponses, setReflectionResponses] = useState([]);
  const [sections, setSections] = useState([]);
  const [spiderData, setSpiderData] = useState({
    labels: [],
    values: [],
    raw: [],
  });

  const gcchfPdfRef = useRef(null);
  const personalPdfRef = useRef(null);
  const filledFormPdfRef = useRef(null);

  const [userName, setUserName] = useState("");
  const [hasReflectionData, setHasReflectionData] = useState(false);
  const [firstAssessmentId, setFirstAssessmentId] = useState(null);
  const [firstAssessmentSections, setFirstAssessmentSections] = useState([]);
  const [isRetake, setIsRetake] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({
    gcchf: false,
    filled: false,
    personal: false,
  });

  const userData = localStorage.getItem("userData");
  const parsedUser = userData ? JSON.parse(userData) : null;
  const peopleId = parsedUser?._id || null;

  const handleSignOut = async () => {
    try {
      await fetch(`${BASE_URL}/api/admin/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }
    localStorage.removeItem("userData");
    localStorage.removeItem("authToken");
    localStorage.removeItem("pendingUser");
    navigate("/sign-in", { replace: true });
  };

  const handleDownload = async (type, pdfRef) => {
    if (!pdfRef?.current?.download) return;

    setDownloadLoading((prev) => ({ ...prev, [type]: true }));
    try {
      await pdfRef.current?.download();
    } catch (err) {
      console.error(`Failed to download ${type} PDF:`, err);
    } finally {
      setTimeout(() => {
        setDownloadLoading((prev) => ({ ...prev, [type]: false }));
      }, 650);
    }
  };

  useEffect(() => {
    if (!assessmentId || !peopleId) return;

    fetch(`${BASE_URL}/api/assessments/history/${peopleId}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success && json.data?.length > 1) {
          const firstId = json.data[0]._id;
          if (firstId !== assessmentId) {
            setFirstAssessmentId(firstId);
            setIsRetake(true);
          }
        }
      })
      .catch((err) =>
        console.error("Failed to fetch assessment history:", err),
      );

    fetch(`${BASE_URL}/api/assessments/assessments-id/${assessmentId}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success && json.data?.people?.full_name) {
          setUserName(json.data.people.full_name);
        }
      })
      .catch((err) => console.error("Failed to fetch person name:", err));

    fetch(`${BASE_URL}/api/response/${assessmentId}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success && json.data?.length > 0) {
          setResponses(json.data);
          const computed = computeSectionAverages(json.data);
          if (computed.length > 0) setSections(computed);
        }
        if (json.reflectionResponses?.length > 0) {
          setReflectionResponses(json.reflectionResponses);
        }
        setHasReflectionData(json.hasReflectionData === true);
      })
      .catch((err) => console.error("Failed to fetch responses:", err));

    fetch(`${BASE_URL}/api/assessments/${assessmentId}/spider-chart`)
      .then((r) => r.json())
      .then((result) => {
        if (result.success) {
          setSpiderData({
            labels: result.labels,
            values: result.values,
            raw: result.data || [],
          });
        }
      })
      .catch((err) => console.error("Failed to fetch spider data:", err));
  }, [assessmentId, peopleId]);

  useEffect(() => {
    if (!firstAssessmentId) return;
    fetch(`${BASE_URL}/api/response/${firstAssessmentId}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success && json.data?.length > 0) {
          const computed = computeSectionAverages(json.data);
          if (computed.length > 0) setFirstAssessmentSections(computed);
        }
      })
      .catch((err) =>
        console.error("Failed to fetch first assessment responses:", err),
      );
  }, [firstAssessmentId]);

  // Block browser back button on the feedback/report page
  useEffect(() => {
    window.history.pushState(null, "", window.location.pathname);
    const blockBack = () => {
      window.history.pushState(null, "", window.location.pathname);
    };
    window.addEventListener("popstate", blockBack);
    return () => window.removeEventListener("popstate", blockBack);
  }, []);

  // Sort responses by subsection display order from spiderData
  const orderedResponses = React.useMemo(() => {
    if (!spiderData.raw.length) return responses;
    const orderMap = {};
    spiderData.raw.forEach((entry, i) => {
      orderMap[entry.name] = i;
    });
    return [...responses].sort((a, b) => {
      const ai = orderMap[a.subsection?.name] ?? 999;
      const bi = orderMap[b.subsection?.name] ?? 999;
      return ai - bi;
    });
  }, [responses, spiderData.raw]);

  const growthStats = React.useMemo(() => {
    if (!isRetake || !sections.length || !firstAssessmentSections.length)
      return null;

    const sectionDeltas = sections
      .map((s) => {
        const first = firstAssessmentSections.find((f) => f.name === s.name);
        return first
          ? {
              name: s.name,
              score: s.score,
              delta: parseFloat((s.score - first.score).toFixed(1)),
            }
          : null;
      })
      .filter(Boolean);

    if (!sectionDeltas.length) return null;

    const overallDelta = parseFloat(
      (
        sectionDeltas.reduce((a, b) => a + b.delta, 0) / sectionDeltas.length
      ).toFixed(1),
    );
    const improvedCount = sectionDeltas.filter((s) => s.delta > 0).length;
    const mostImproved = sectionDeltas.reduce((a, b) =>
      b.delta > a.delta ? b : a,
    );
    const focusArea = sectionDeltas.reduce((a, b) =>
      b.delta < a.delta ? b : a,
    );

    let headline, body;

    if (overallDelta >= 1.0) {
      headline = "Outstanding Growth";
      body = `You have made remarkable progress across your church's health. Your commitment and dedication is producing real, visible transformation in your community, and this framework highlights the meaningful growth taking place.`;
    } else if (overallDelta >= 0.5) {
      headline = "Real Progress";
      body = `Your investment in church health is paying off. ${improvedCount} out of ${sectionDeltas.length} areas have grown since your first review — this is encouraging momentum worth celebrating.`;
    } else if (overallDelta > 0) {
      headline = "Moving Forward";
      body = `Every point of growth matters. You are moving in the right direction, and the steady work you are doing is building a stronger, healthier church community over time through this framework.`;
    } else if (overallDelta === 0) {
      headline = "Steady and Consistent";
      body = `Maintaining church health is itself a significant achievement. Your community has remained stable — use these insights to identify your next intentional areas of focus.`;
    } else {
      headline = "Clarity for Growth";
      body = `Every honest framework review is a gift. The areas highlighted here are not failures — they are clear opportunities for focused, intentional growth in the season ahead.`;
    }
    return {
      overallDelta,
      improvedCount,
      mostImproved,
      focusArea,
      headline,
      body,
      total: sectionDeltas.length,
    };
  }, [isRetake, sections, firstAssessmentSections]);

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, sm: 3 } }}>
      {/* Assessment Complete Banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          bgcolor: "#fff",
          border: "1px solid #e8eaed",
          borderRadius: 2,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          mt: 3,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "#eaf6ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          <CheckCircleIcon sx={{ color: "#20A15C", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 17,
              fontWeight: 700,
              color: "#1a202c",
              mb: 0.4,
            }}
          >
            Framework Completed{userName ? `, ${userName}` : ""}!
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: 13, color: "#6b7280" }}>
            Thank you for your honest reflection. Your results are processed and
            ready for review below.
          </Typography>
        </Box>
      </Box>

      {/* Your Church Health Overview */}
      <Typography
        sx={{
          fontFamily: font,
          fontSize: 18,
          fontWeight: 700,
          color: "#1a202c",
          mb: 2.5,
        }}
      >
        Your Church Health Overview
      </Typography>

      {isRetake ? (
        /* ══ RETAKE: 2-column side-by-side comparison ══ */
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* ── LEFT: First Assessment ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
            }}
          >
            {/* Label */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                bgcolor: "#fef9c3",
                border: "1px solid #fde68a",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                alignSelf: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#ca8a04",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#854d0e",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                }}
              >
                previous scores View
              </Typography>
            </Box>

            {/* Radar — First */}
            <Box
              sx={{
                bgcolor: "#fff",
                border: "1px solid #e8eaed",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#9ca3af",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Radar Chart
              </Typography>
              <Box
                sx={{
                  "& canvas": {
                    maxHeight: {
                      xs: "260px !important",
                      md: "340px !important",
                    },
                  },
                }}
              >
                <SpiderChartContainer assessmentId={firstAssessmentId} />
              </Box>
            </Box>

            {/* Section Averages — First */}
            <Box
              sx={{
                bgcolor: "#fff",
                border: "1px solid #e8eaed",
                borderRadius: 2,
                p: 3,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Section Averages
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {firstAssessmentSections.map((s) => (
                  <Box key={s.name}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 0.8,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 13,
                            height: 13,
                            borderRadius: "50%",
                            bgcolor: s.color,
                            flexShrink: 0,
                            mt: 0.3,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: font,
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#1a202c",
                            lineHeight: 1.3,
                            textTransform: "uppercase",
                          }}
                        >
                          {s.name}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#1a202c",
                          ml: 1,
                          flexShrink: 0,
                        }}
                      >
                        {s.score}/5
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(s.score / 5) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#e5e7eb",
                        mb: 1.2,
                        "& .MuiLinearProgress-bar": {
                          bgcolor: s.color,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
              {/* Gospel Assurance — inside Latest section averages */}
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #e8eaed" }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#1a202c",
                    mb: 1,
                  }}
                >
                  Gospel Assurance: Our Sonship and security in Christ shapes
                  how we view ministry performance.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    color: "#374151",
                    lineHeight: 1.8,
                  }}
                >
                  We can celebrate success without pride or self-reliance.
                  <br />
                  We can acknowledge shortcomings without shame.
                  <br />
                  We can labour with all our strength and yet, at the same time,
                  also rest in Christ.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── RIGHT: Latest Assessment ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
            }}
          >
            {/* Label */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                bgcolor: "#dbeafe",
                border: "1px solid #bfdbfe",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                alignSelf: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#3b82f6",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#1e40af",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                }}
              >
                Latest scores View
              </Typography>
            </Box>

            {/* Radar — Latest */}
            <Box
              sx={{
                bgcolor: "#fff",
                border: "1px solid #e8eaed",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#9ca3af",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Radar Chart
              </Typography>
              <Box
                id="spider-chart"
                sx={{
                  "& canvas": {
                    maxHeight: {
                      xs: "260px !important",
                      md: "340px !important",
                    },
                  },
                }}
              >
                <SpiderChartContainer assessmentId={assessmentId} />
              </Box>
            </Box>

            {/* Section Averages — Latest (with delta + Gospel Assurance) */}
            <Box
              sx={{
                bgcolor: "#fff",
                border: "1px solid #e8eaed",
                borderRadius: 2,
                p: 3,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Section Averages
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sections.map((s) => {
                  const prev = firstAssessmentSections.find(
                    (f) => f.name === s.name,
                  );
                  const delta =
                    prev != null
                      ? parseFloat((s.score - prev.score).toFixed(1))
                      : null;
                  const deltaColor =
                    delta === null
                      ? "#9ca3af"
                      : delta > 0
                        ? "#16a34a"
                        : delta < 0
                          ? "#dc2626"
                          : "#6b7280";
                  const deltaBg =
                    delta === null || delta === 0
                      ? "#f3f4f6"
                      : delta > 0
                        ? "#dcfce7"
                        : "#fee2e2";
                  const deltaLabel =
                    delta === null
                      ? null
                      : delta > 0
                        ? `+${delta}`
                        : delta === 0
                          ? "±0"
                          : `${delta}`;

                  return (
                    <Box key={s.name}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 0.8,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 13,
                              height: 13,
                              borderRadius: "50%",
                              bgcolor: s.color,
                              flexShrink: 0,
                              mt: 0.3,
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#1a202c",
                              lineHeight: 1.3,
                              textTransform: "uppercase",
                            }}
                          >
                            {s.name}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                            ml: 1,
                          }}
                        >
                          {deltaLabel && (
                            <Box
                              sx={{
                                bgcolor: deltaBg,
                                borderRadius: 1,
                                px: 0.8,
                                py: 0.15,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: font,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: deltaColor,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {deltaLabel}
                              </Typography>
                            </Box>
                          )}
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#1a202c",
                            }}
                          >
                            {s.score}/5
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(s.score / 5) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "#e5e7eb",
                          mb: 1.2,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: s.color,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Gospel Assurance — inside Latest section averages */}
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #e8eaed" }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#1a202c",
                    mb: 1,
                  }}
                >
                  Gospel Assurance: Our Sonship and security in Christ shapes
                  how we view ministry performance.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    color: "#374151",
                    lineHeight: 1.8,
                  }}
                >
                  We can celebrate success without pride or self-reliance.
                  <br />
                  We can acknowledge shortcomings without shame.
                  <br />
                  We can labour with all our strength and yet, at the same time,
                  also rest in Christ.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* ══ SINGLE ASSESSMENT: chart left, sections right ══ */
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 4,
            alignItems: "stretch",
          }}
        >
          {/* Radar */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#fff",
              border: "1px solid #e8eaed",
              borderRadius: 2,
              p: 2,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 10,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Radar Chart
            </Typography>
            <Box
              id="spider-chart"
              sx={{
                "& canvas": {
                  maxHeight: { xs: "280px !important", md: "380px !important" },
                },
              }}
            >
              <SpiderChartContainer assessmentId={assessmentId} />
            </Box>
          </Box>

          {/* Section Averages + Gospel Assurance */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#fff",
              border: "1px solid #e8eaed",
              borderRadius: 2,
              p: 3,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Section Averages
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {sections.map((s) => (
                <Box key={s.name}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 13,
                          height: 13,
                          borderRadius: "50%",
                          bgcolor: s.color,
                          flexShrink: 0,
                          mt: 0.3,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#1a202c",
                          lineHeight: 1.3,
                          textTransform: "uppercase",
                        }}
                      >
                        {s.name}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#1a202c",
                        ml: 1,
                        flexShrink: 0,
                      }}
                    >
                      {s.score}/5
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(s.score / 5) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#e5e7eb",
                      mb: 1.2,
                      "& .MuiLinearProgress-bar": {
                        bgcolor: s.color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Gospel Assurance — inside section averages */}
            <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #e8eaed" }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1a202c",
                  mb: 1,
                }}
              >
                Gospel Assurance: Our Sonship and security in Christ shapes how
                we view ministry performance.
              </Typography>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 14,
                  color: "#374151",
                  lineHeight: 1.8,
                }}
              >
                We can celebrate success without pride or self-reliance.
                <br />
                We can acknowledge shortcomings without shame.
                <br />
                We can labour with all our strength and yet, at the same time,
                also rest in Christ.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Growth Encouragement Card */}
      {growthStats && (
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e8eaed",
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Your Growth Journey
            </Typography>

            {/* Overall delta pill */}
            <Box
              sx={{
                bgcolor:
                  growthStats.overallDelta > 0
                    ? "#dcfce7"
                    : growthStats.overallDelta < 0
                      ? "#fee2e2"
                      : "#f3f4f6",
                borderRadius: 1.5,
                px: 1.5,
                py: 0.4,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 12,
                  fontWeight: 700,
                  color:
                    growthStats.overallDelta > 0
                      ? "#16a34a"
                      : growthStats.overallDelta < 0
                        ? "#dc2626"
                        : "#6b7280",
                }}
              >
                {growthStats.overallDelta > 0
                  ? `+${growthStats.overallDelta}`
                  : growthStats.overallDelta === 0
                    ? "No change"
                    : `${growthStats.overallDelta}`}
              </Typography>
              <Typography
                sx={{ fontFamily: font, fontSize: 11, color: "#6b7280" }}
              >
                overall
              </Typography>
            </Box>
          </Box>

          {/* Headline + body message */}
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 16,
              fontWeight: 700,
              color: "#1a202c",
              mb: 0.6,
            }}
          >
            {growthStats.headline}
          </Typography>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#374151",
              lineHeight: 1.8,
              mb: 2.5,
            }}
          >
            {growthStats.body}
          </Typography>

          {/* Highlight cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            {/* Most improved */}
            {growthStats.mostImproved.delta > 0 && (
              <Box
                sx={{
                  flex: 1,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 1.5,
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#15803d",
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    mb: 0.6,
                  }}
                >
                  Most Improved
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#14532d",
                    mb: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  {growthStats.mostImproved.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 12,
                    color: "#16a34a",
                    fontWeight: 600,
                  }}
                >
                  +{growthStats.mostImproved.delta} points since first Framework
                </Typography>
              </Box>
            )}

            {/* Focus area */}
            <Box
              sx={{
                flex: 1,
                bgcolor: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 1.5,
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#b45309",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  mb: 0.6,
                }}
              >
                Keep Investing
              </Typography>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#78350f",
                  mb: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {growthStats.focusArea.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 12,
                  color: "#92400e",
                  fontWeight: 600,
                }}
              >
                {growthStats.focusArea.score}/5 — continued focus here will make
                a lasting difference
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Download Your Reports */}
      <Typography
        sx={{
          fontFamily: font,
          fontSize: 18,
          fontWeight: 700,
          color: "#1a202c",
          mb: 2,
        }}
      >
        Download Your Reports
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1.5 }}>
        {/* Row 1 - Health Report */}
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e8eaed",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                bgcolor: "#0ea5e9",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: font,
                }}
              >
                PDF
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1a202c",
                  mb: 0.3,
                }}
              >
                Download Your GCCHF Health Report (PDF)
              </Typography>
              <Typography
                sx={{ fontFamily: font, fontSize: 11, color: "#6b7280" }}
              >
                Your comprehensive church health analysis, featuring the spider
                chart, sectional averages.
              </Typography>
            </Box>
            <IconButton
              onClick={() => handleDownload("gcchf", gcchfPdfRef)}
              disabled={downloadLoading.gcchf}
              sx={{ color: "#1e293b", flexShrink: 0 }}
            >
              {downloadLoading.gcchf ? (
                <CircularProgress size={20} sx={{ color: "#1e293b" }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Row 2 - Filled Form */}
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e8eaed",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                bgcolor: "#0ea5e9",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: font,
                }}
              >
                PDF
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1a202c",
                  mb: 0.3,
                }}
              >
                Download Your Filled Form (PDF)
              </Typography>
              <Typography
                sx={{ fontFamily: font, fontSize: 11, color: "#6b7280" }}
              >
                A complete, read-only transcript of all your objective scores
                and qualitative reflections for your records.
              </Typography>
            </Box>
            <IconButton
              onClick={() => handleDownload("filled", filledFormPdfRef)}
              disabled={downloadLoading.filled}
              sx={{ color: "#1e293b", flexShrink: 0 }}
            >
              {downloadLoading.filled ? (
                <CircularProgress size={20} sx={{ color: "#1e293b" }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Row 3 - Personal Health Report */}
        <Box
          sx={{
            bgcolor: hasReflectionData ? "#fff" : "#fafafa",
            border: "1px solid #e8eaed",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                bgcolor: hasReflectionData ? "#0ea5e9" : "#d1d5db",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: font,
                }}
              >
                PDF
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: hasReflectionData ? "#1a202c" : "#9ca3af",
                  mb: 0.3,
                }}
              >
                Download Your Personal Health Report (PDF)
              </Typography>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  color: hasReflectionData ? "#6b7280" : "#9ca3af",
                }}
              >
                A personalized look at your leadership growth and strengths
                based on your self-reflective ratings.
              </Typography>
            </Box>
            <IconButton
              disabled={!hasReflectionData || downloadLoading.personal}
              onClick={
                hasReflectionData
                  ? () => handleDownload("personal", personalPdfRef)
                  : undefined
              }
              sx={{
                color: hasReflectionData ? "#1e293b" : "#9ca3af",
                flexShrink: 0,
              }}
            >
              {downloadLoading.personal ? (
                <CircularProgress size={20} sx={{ color: "#1e293b" }} />
              ) : hasReflectionData ? (
                <FileDownloadOutlinedIcon />
              ) : (
                <LockOutlinedIcon />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Unlock note */}
      <Typography
        sx={{ fontFamily: font, fontSize: 11, color: "#6b7280", mb: 4 }}
      >
        *Complete all self-reflection ratings during the Framework to unlock{" "}
        <Box component="span" sx={{ fontWeight: 700, color: "#1a202c" }}>
          Your Personal Health Report
        </Box>
        .
      </Typography>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 6,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              fontWeight: 700,
              color: "#1a202c",
              mb: 0.3,
            }}
          >
            Need help interpreting these results?
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: 12, color: "#6b7280" }}>
            Contact CTCAP for coaching, resources and next steps for your
            church.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<HeadsetMicOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => window.open(`mailto:${CTCAP_EMAIL}`, "_blank")}
            sx={{
              borderColor: "#1e293b",
              color: "#1e293b",
              fontFamily: font,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 1,
              px: 2,
              py: 1,
              boxShadow: "none",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              "&:hover": {
                borderColor: "#c41825",
                color: "#c41825",
                background: "none",
              },
            }}
          >
            Contact CTCAP
          </Button>
          <Button
            variant="contained"
            startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/verification", { replace: true })}
            sx={{
              bgcolor: "#1e293b",

              fontFamily: font,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 1,
              px: 2,
              py: 1,
              boxShadow: "none",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Retake Framework
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="text"
          startIcon={<LogoutIcon sx={{ fontSize: 14 }} />}
          onClick={handleSignOut}
          sx={{
            color: "#ED1C2E",
            fontFamily: font,
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 1,
            px: 2,
            py: 1,
            boxShadow: "none",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            m: 4,
            "&:hover": { background: "none" },
          }}
        >
          Sign Out
        </Button>
      </Box>

      {/* PDF Components */}
      <GcchfHealthReportPdf
        ref={gcchfPdfRef}
        spiderData={spiderData}
        sections={sections}
        userName={userName}
      />
      <PersonalHealthReportPdf
        ref={personalPdfRef}
        reflectionResponses={reflectionResponses}
        sections={sections}
        userName={userName}
      />
      <FilledFormPDF
        ref={filledFormPdfRef}
        responses={orderedResponses}
        userName={userName}
      />
    </Box>
  );
};

export default Feedback;
